import { Permission, type PermissionValue } from "../auth/types.ts";
import type { AppState } from "../../store/app-state.ts";
import type { ProjectDataResourceId } from "../sync/project-data-requirements.ts";
import {
    type SupportedPromptBookCueType,
} from "./cue-creation.ts";
import type { CueAnchor, CueDepartment } from "./types.ts";

export interface CueSourceData {
    found: boolean;
    label: string;
    cueNumber: string;
    notes: string;
    department: CueDepartment | null;
    tab: string;
    sourceId: string;
}

export interface CueSourceCreateInput {
    cueNumber: string;
    notes: string;
    sceneId: string;
    afterSourceId?: string;
}

export interface CueSourceUpdate {
    cueNumber: string;
    notes: string;
}

export type CueSourceReadState = Pick<
    AppState,
    'lightingCues' | 'soundCues' | 'followSpotCues' | 'flyCues' | 'projectionCues' | 'rows'
>;

export interface CueSourceReadAdapter {
    resourceId: ProjectDataResourceId;
    tab: string;
    list: (state: CueSourceReadState) => CueSourceData[];
}

export interface CueSourceAdapter extends CueSourceReadAdapter {
    requiredPermission: PermissionValue;
    create: (state: AppState, input: CueSourceCreateInput) => string;
    update: (state: AppState, sourceId: string, input: CueSourceUpdate) => void;
}

function snapshot(
    department: CueDepartment,
    sourceId: string,
    cueNumber: string,
    notes: string | undefined,
    tab: string,
    label: string = cueNumber,
): CueSourceData {
    return {
        found: true,
        label,
        cueNumber,
        notes: notes ?? '',
        department,
        tab,
        sourceId,
    };
}

export function getDeckCueDisplayLabel(row: CueSourceReadState['rows'][number]): string {
    return row.item || row.number || 'Deck Cue';
}

function missing(department: CueDepartment, sourceId: string, tab: string): CueSourceData {
    return {
        found: false,
        label: `${department.toUpperCase()} (deleted)`,
        cueNumber: '',
        notes: '',
        department,
        tab,
        sourceId,
    };
}

const EMPTY_CUE_SOURCE_DATA: CueSourceData = {
    found: false,
    label: '',
    cueNumber: '',
    notes: '',
    department: null,
    tab: '',
    sourceId: '',
};

function sortOrderForInsertion(
    records: readonly { id: string; sceneId: string; sortOrder: number; deleted?: boolean }[],
    sceneId: string,
    afterSourceId?: string,
): number {
    const sceneRecords = records
        .filter(record => !record.deleted && record.sceneId === sceneId)
        .toSorted((left, right) => left.sortOrder - right.sortOrder);
    const predecessorIndex = sceneRecords.findIndex(record => record.id === afterSourceId);

    if (predecessorIndex < 0) return sceneRecords[0] ? sceneRecords[0].sortOrder - 1 : 0;

    const predecessor = sceneRecords[predecessorIndex];
    if (!predecessor) return 0;
    const successor = sceneRecords[predecessorIndex + 1];
    return successor ? (predecessor.sortOrder + successor.sortOrder) / 2 : predecessor.sortOrder + 1;
}

const CUE_SOURCE_READ_ADAPTERS: Record<CueDepartment, CueSourceReadAdapter> = {
    lx: {
        resourceId: 'lighting',
        tab: 'lighting',
        list: state => state.lightingCues
            .filter(cue => !cue.deleted)
            .map(cue => snapshot('lx', cue.id, cue.cueNumber, cue.notes, 'lighting')),
    },
    sq: {
        resourceId: 'sound',
        tab: 'sound',
        list: state => state.soundCues
            .filter(cue => !cue.deleted)
            .map(cue => snapshot('sq', cue.id, cue.cueNumber, cue.notes, 'sound')),
    },
    fs: {
        resourceId: 'lighting',
        tab: 'lighting',
        list: state => state.followSpotCues
            .filter(cue => !cue.deleted)
            .map(cue => snapshot('fs', cue.id, cue.cueNumber, cue.notes, 'lighting')),
    },
    deck: {
        resourceId: 'showStructure',
        tab: 'scenes',
        list: state => state.rows
            .filter(row => !row.deleted && row.isDeckCue)
            .map(row => snapshot('deck', row.id, row.number, row.notes, 'scenes', getDeckCueDisplayLabel(row))),
    },
    fly: {
        resourceId: 'fly',
        tab: 'scenes',
        list: state => state.flyCues
            .filter(cue => !cue.deleted)
            .map(cue => snapshot('fly', cue.id, cue.cueNumber, cue.notes, 'scenes')),
    },
    av: {
        resourceId: 'projections',
        tab: 'projections',
        list: state => state.projectionCues
            .filter(cue => !cue.deleted)
            .map(cue => snapshot('av', cue.id, cue.cueNumber, cue.notes, 'projections')),
    },
};

const CUE_SOURCE_ADAPTERS: Record<SupportedPromptBookCueType, CueSourceAdapter> = {
    lx: {
        ...CUE_SOURCE_READ_ADAPTERS.lx,
        requiredPermission: Permission.EDIT_LIGHTING,
        create: (state, input) => state.lightingActions.addLightingCue({
            sceneId: input.sceneId,
            cueNumber: input.cueNumber,
            notes: input.notes,
            sortOrder: sortOrderForInsertion(state.lightingCues, input.sceneId, input.afterSourceId),
        }),
        update: (state, sourceId, input) => state.lightingActions.updateLightingCue(sourceId, input),
    },
    sq: {
        ...CUE_SOURCE_READ_ADAPTERS.sq,
        requiredPermission: Permission.EDIT_SOUND,
        create: (state, input) => state.soundActions.addSoundCue({
            sceneId: input.sceneId,
            cueNumber: input.cueNumber,
            notes: input.notes,
            sortOrder: sortOrderForInsertion(state.soundCues, input.sceneId, input.afterSourceId),
        }),
        update: (state, sourceId, input) => state.soundActions.updateSoundCue(sourceId, input),
    },
    fs: {
        ...CUE_SOURCE_READ_ADAPTERS.fs,
        requiredPermission: Permission.EDIT_LIGHTING,
        create: (state, input) => state.lightingActions.addFollowSpotCue({
            sceneId: input.sceneId,
            cueNumber: input.cueNumber,
            notes: input.notes,
            sortOrder: sortOrderForInsertion(state.followSpotCues, input.sceneId, input.afterSourceId),
        }),
        update: (state, sourceId, input) => state.lightingActions.updateFollowSpotCue(sourceId, input),
    },
    deck: {
        ...CUE_SOURCE_READ_ADAPTERS.deck,
        requiredPermission: Permission.EDIT_RUNSHEET,
        create: (state, input) => {
            const sameScenePredecessor = state.rows.find(row => (
                row.id === input.afterSourceId && row.sceneId === input.sceneId && !row.deleted
            ));
            const sourceId = state.runSheetActions.addRow(input.sceneId, sameScenePredecessor?.id);
            state.runSheetActions.updateRow(sourceId, {
                number: input.cueNumber,
                notes: input.notes,
                isDeckCue: true,
            });
            if (!sameScenePredecessor) state.runSheetActions.moveRow(sourceId, input.sceneId, 0);
            return sourceId;
        },
        update: (state, sourceId, input) => state.runSheetActions.updateRow(sourceId, {
            number: input.cueNumber,
            notes: input.notes,
        }),
    },
};

export function getCueSourceAdapter(cueType: SupportedPromptBookCueType): CueSourceAdapter {
    return CUE_SOURCE_ADAPTERS[cueType];
}

export function getCueSourceReadAdapter(cueType: CueDepartment): CueSourceReadAdapter {
    return CUE_SOURCE_READ_ADAPTERS[cueType];
}

export function getCueSourceResourceId(cueType: CueDepartment): ProjectDataResourceId {
    return getCueSourceReadAdapter(cueType).resourceId;
}

export function listCueSourceData(state: CueSourceReadState, cueType: CueDepartment): CueSourceData[] {
    return getCueSourceReadAdapter(cueType).list(state);
}

export function resolveCueSourceData(state: CueSourceReadState, anchor: CueAnchor | null | undefined): CueSourceData {
    if (!anchor) return EMPTY_CUE_SOURCE_DATA;

    const adapter = getCueSourceReadAdapter(anchor.cueType);
    return adapter.list(state).find(source => source.sourceId === anchor.sourceId)
        ?? missing(anchor.cueType, anchor.sourceId, adapter.tab);
}
